<script>
import VIcon from '@/components/Icon.vue';

const OUTPUT_SIZE = 512;
const JPEG_QUALITY = 0.85;
const MAX_ZOOM = 3;

export default {
  name: 'AvatarEditorModal',
  components: {
    VIcon
  },
  props: {
    src: {
      type: String,
      required: true
    },
    fileName: {
      type: String,
      default: 'avatar.jpg'
    },
    uploading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
      imgWidth: 0,
      imgHeight: 0,
      stageSize: 280,
      cropSize: 210,
      dragging: false,
      dragStart: null,
      pinchStart: null,
      workingSrc: '',
      applying: false
    };
  },
  computed: {
    displaySrc() {
      return this.workingSrc || this.src;
    },
    baseScale() {
      if (!this.imgWidth || !this.imgHeight) {
        return 1;
      }
      return this.cropSize / Math.min(this.imgWidth, this.imgHeight);
    },
    displayWidth() {
      return this.imgWidth * this.baseScale * this.zoom;
    },
    displayHeight() {
      return this.imgHeight * this.baseScale * this.zoom;
    },
    imageStyle() {
      return {
        width: `${this.displayWidth}px`,
        height: `${this.displayHeight}px`,
        left: '50%',
        top: '50%',
        marginLeft: `${-this.displayWidth / 2 + this.offsetX}px`,
        marginTop: `${-this.displayHeight / 2 + this.offsetY}px`
      };
    },
    circleStyle() {
      return {
        width: `${this.cropSize}px`,
        height: `${this.cropSize}px`
      };
    },
    isBusy() {
      return this.applying || this.uploading;
    }
  },
  mounted() {
    document.body.classList.add('no-scroll');
    this.updateMetrics();
    window.addEventListener('resize', this.updateMetrics);
    window.addEventListener('mousemove', this.onDrag);
    window.addEventListener('mouseup', this.endDrag);
    window.addEventListener('touchmove', this.onDrag, { passive: false });
    window.addEventListener('touchend', this.endDrag);
    window.addEventListener('keydown', this.onKeydown);
    this.$nextTick(() => {
      if (this.$refs.stage) {
        this.$refs.stage.addEventListener('wheel', this.onWheel, { passive: false });
      }
    });
  },
  beforeDestroy() {
    document.body.classList.remove('no-scroll');
    window.removeEventListener('resize', this.updateMetrics);
    window.removeEventListener('mousemove', this.onDrag);
    window.removeEventListener('mouseup', this.endDrag);
    window.removeEventListener('touchmove', this.onDrag);
    window.removeEventListener('touchend', this.endDrag);
    window.removeEventListener('keydown', this.onKeydown);
    if (this.$refs.stage) {
      this.$refs.stage.removeEventListener('wheel', this.onWheel);
    }
    this.revokeWorkingSrc();
  },
  methods: {
    revokeWorkingSrc() {
      if (this.workingSrc) {
        URL.revokeObjectURL(this.workingSrc);
        this.workingSrc = '';
      }
    },
    updateMetrics() {
      const { stage } = this.$refs;
      if (!stage) {
        return;
      }
      this.stageSize = stage.clientWidth;
      this.cropSize = Math.round(this.stageSize * 0.75);
      this.clampOffset();
    },
    onImageLoad() {
      const { image: img } = this.$refs;
      if (!img) {
        return;
      }
      this.imgWidth = img.naturalWidth;
      this.imgHeight = img.naturalHeight;
      this.zoom = 1;
      this.offsetX = 0;
      this.offsetY = 0;
      this.$nextTick(this.updateMetrics);
    },
    maxOffset() {
      return {
        x: Math.max(0, (this.displayWidth - this.cropSize) / 2),
        y: Math.max(0, (this.displayHeight - this.cropSize) / 2)
      };
    },
    clampOffset() {
      const max = this.maxOffset();
      this.offsetX = Math.min(max.x, Math.max(-max.x, this.offsetX));
      this.offsetY = Math.min(max.y, Math.max(-max.y, this.offsetY));
    },
    setZoom(value) {
      this.zoom = Math.min(MAX_ZOOM, Math.max(1, value));
      this.clampOffset();
    },
    nudgeZoom(delta) {
      this.setZoom(this.zoom + delta);
    },
    getPoint(event) {
      if (event.touches && event.touches.length) {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
      if (event.changedTouches && event.changedTouches.length) {
        return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
      }
      return { x: event.clientX, y: event.clientY };
    },
    pinchDistance(event) {
      if (!event.touches || event.touches.length < 2) {
        return 0;
      }
      const dx = event.touches[0].clientX - event.touches[1].clientX;
      const dy = event.touches[0].clientY - event.touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },
    startDrag(event) {
      if (event.touches && event.touches.length === 2) {
        this.pinchStart = {
          distance: this.pinchDistance(event),
          zoom: this.zoom
        };
        this.dragging = false;
        return;
      }
      const point = this.getPoint(event);
      this.dragging = true;
      this.dragStart = {
        x: point.x,
        y: point.y,
        ox: this.offsetX,
        oy: this.offsetY
      };
    },
    onDrag(event) {
      if (event.touches && event.touches.length === 2 && this.pinchStart) {
        event.preventDefault();
        const distance = this.pinchDistance(event);
        if (distance && this.pinchStart.distance) {
          this.setZoom(this.pinchStart.zoom * (distance / this.pinchStart.distance));
        }
        return;
      }
      if (!this.dragging || !this.dragStart) {
        return;
      }
      event.preventDefault();
      const point = this.getPoint(event);
      this.offsetX = this.dragStart.ox + (point.x - this.dragStart.x);
      this.offsetY = this.dragStart.oy + (point.y - this.dragStart.y);
      this.clampOffset();
    },
    endDrag() {
      this.dragging = false;
      this.dragStart = null;
      this.pinchStart = null;
    },
    onWheel(event) {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.08 : 0.08;
      this.setZoom(this.zoom + delta);
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('cancel');
      }
    },
    rotateImage() {
      const { image: img } = this.$refs;
      if (!img || !img.naturalWidth) {
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalHeight;
      canvas.height = img.naturalWidth;
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      canvas.toBlob(blob => {
        if (!blob) {
          return;
        }
        this.revokeWorkingSrc();
        this.workingSrc = URL.createObjectURL(blob);
      }, 'image/jpeg', 0.95);
    },
    apply() {
      const { image: img } = this.$refs;
      if (!img || !img.naturalWidth || this.applying) {
        return;
      }
      this.applying = true;
      const scale = this.baseScale * this.zoom;
      const cropLeft = (this.stageSize - this.cropSize) / 2;
      const cropTop = (this.stageSize - this.cropSize) / 2;
      const imgLeft = this.stageSize / 2 - this.displayWidth / 2 + this.offsetX;
      const imgTop = this.stageSize / 2 - this.displayHeight / 2 + this.offsetY;
      const sx = (cropLeft - imgLeft) / scale;
      const sy = (cropTop - imgTop) / scale;
      const sSize = this.cropSize / scale;
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      canvas.toBlob(blob => {
        this.applying = false;
        if (!blob) {
          this.$emit('error');
          return;
        }
        const name = (this.fileName || 'avatar.jpg').replace(/\.[^.]+$/, '.jpg');
        this.$emit('apply', new File([blob], name, { type: 'image/jpeg' }));
      }, 'image/jpeg', JPEG_QUALITY);
    }
  }
};
</script>

<template>
  <div class="avatar-editor">
    <div
      class="avatar-editor__overlay"
      @click="!isBusy && $emit('cancel')"
    />
    <div
      class="avatar-editor__card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="avatar-editor-title"
    >
      <div v-if="uploading" class="avatar-editor__card-loading">
        <v-icon class="avatar-editor__card-loading__icon" name="loading" />
      </div>
      <button
        type="button"
        class="avatar-editor__close"
        aria-label="Fechar"
        :disabled="isBusy"
        @click="$emit('cancel')"
      >
        <v-icon name="close" />
      </button>
      <h2 id="avatar-editor-title" class="avatar-editor__title">Ajustar imagem</h2>
      <p class="avatar-editor__hint">Arraste para reposicionar. Use o zoom para enquadrar.</p>
      <div
        ref="stage"
        class="avatar-editor__stage"
        :class="{ 'is-dragging': dragging, 'is-disabled': isBusy }"
        @mousedown.prevent="!isBusy && startDrag($event)"
        @touchstart.prevent="!isBusy && startDrag($event)"
      >
        <img
          ref="image"
          :src="displaySrc"
          class="avatar-editor__image"
          :style="imageStyle"
          alt="Prévia do avatar"
          draggable="false"
          @load="onImageLoad"
        />
        <div class="avatar-editor__circle" :style="circleStyle" />
      </div>
      <div class="avatar-editor__zoom">
        <button
          type="button"
          class="avatar-editor__zoom-btn"
          aria-label="Diminuir zoom"
          :disabled="isBusy"
          @click="nudgeZoom(-0.1)"
        >
          −
        </button>
        <input
          class="avatar-editor__range"
          type="range"
          min="1"
          :max="3"
          step="0.01"
          :value="zoom"
          :disabled="isBusy"
          @input="setZoom(Number($event.target.value))"
        />
        <button
          type="button"
          class="avatar-editor__zoom-btn"
          aria-label="Aumentar zoom"
          :disabled="isBusy"
          @click="nudgeZoom(0.1)"
        >
          +
        </button>
      </div>
      <button
        type="button"
        class="avatar-editor__rotate"
        :disabled="isBusy"
        @click="rotateImage"
      >
        <v-icon name="refresh-icon" class="avatar-editor__rotate-icon" />
        Girar
      </button>
      <div class="avatar-editor__actions">
        <button
          type="button"
          class="avatar-editor__cancel"
          :disabled="isBusy"
          @click="$emit('cancel')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="avatar-editor__apply"
          :class="{ 'avatar-editor__apply--loading': isBusy }"
          :disabled="isBusy || !imgWidth"
          @click="apply"
        >
          <v-icon
            v-if="isBusy"
            class="avatar-editor__apply__icon"
            name="loading"
          />
          <span v-else>Aplicar</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.avatar-editor {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;

  &__overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.45);
  }

  &__card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 24rem;
    background: var(--color-white);
    border-radius: 0.5rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    padding: 1.75rem 1.5rem 1.25rem;
  }

  &__card-loading {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.72);
    border-radius: 0.5rem;

    &__icon {
      animation: spin 1.2s infinite linear;
      color: var(--color-primary);
      size: 2rem;
    }
  }

  &__close {
    position: absolute;
    top: 0.85rem;
    right: 0.85rem;
    size: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-grey-dark);
    background: transparent;
    cursor: pointer;

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }

    svg {
      size: 0.85rem;
    }
  }

  &__title {
    color: var(--color-primary);
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.4;
    padding-right: 1.5rem;
  }

  &__hint {
    color: var(--color-grey-medium);
    font-size: 0.8125rem;
    margin: 0.25rem 0 1rem;
  }

  &__stage {
    position: relative;
    width: 100%;
    height: 17.5rem;
    overflow: hidden;
    background: #111;
    border-radius: 0.5rem;
    cursor: grab;
    touch-action: none;

    &.is-dragging {
      cursor: grabbing;
    }

    &.is-disabled {
      cursor: default;
      pointer-events: none;
    }
  }

  &__image {
    position: absolute;
    max-width: none;
    pointer-events: none;
    user-select: none;
  }

  &__circle {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border: 0.125rem solid var(--color-white);
    border-radius: 50%;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
    pointer-events: none;
  }

  &__zoom {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1rem;
  }

  &__zoom-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    size: 1.75rem;
    color: var(--color-primary);
    background: transparent;
    font-size: 1.25rem;
    line-height: 1;
    cursor: pointer;
  }

  &__range {
    flex: 1;
    height: 0.25rem;
    accent-color: var(--color-primary);
    cursor: pointer;
  }

  &__rotate {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.85rem;
    color: var(--color-primary);
    background: transparent;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
  }

  &__rotate-icon {
    size: 1rem;
  }

  &__actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.25rem;
  }

  &__cancel,
  &__apply {
    flex: 1;
    height: 2.75rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
    cursor: pointer;
  }

  &__cancel {
    background: var(--color-white);
    border: 0.0625rem solid var(--color-primary);
    color: var(--color-primary);

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  &__apply {
    background: var(--color-primary);
    color: var(--color-white);
    display: flex;
    align-items: center;
    justify-content: center;

    &__icon {
      animation: spin 1.2s infinite linear;
      size: 1.25rem;
    }

    &--loading {
      background: var(--color-primary);
      color: var(--color-white);
    }

    &:disabled:not(&--loading) {
      cursor: default;
      background-color: var(--color-grey-lighten);
      color: var(--color-grey-medium);
    }
  }
}
</style>
